import { type FC, useCallback } from "react";
import { useHistory } from "react-router-dom";

import { useConfig } from "@jti/app-context/useConfig";
import { useCheckoutConfiguration } from "@jti/commerce/providers";
import { hasOngoingSubscriptionError } from "@jti/commerce/shared";
import { isServerSide, standardCheckoutRoutes } from "@jti/fe-common";
import { AUTH_ROUTE_PATHS } from "@jti/fe-common/routes/auth-routes";
import { ICartItemErrorType } from "@jti/magento";
import { getSessionStorage, getUser } from "@jti/rest-api";
import { useDispatch, useSelector } from "@jti/store/reduxHooks";
import { selectIsCartOnlyStandaloneGifts } from "@jti/store/selectors/selectIsCartOnlyStandaloneGifts";
import { selectQtyPending } from "@jti/store/selectors/selectQtyPending";
import { clearFreeGiftsErrors } from "@jti/store/slices/cartSlice";
import { checkoutV2Slice } from "@jti/store/slices/checkoutV2Slice";
import { setReady } from "@jti/store/slices/standardCheckoutFormSlice";
import { redirectToAuthWithBack } from "@jti/store/utils";
import {
  LoyaltyReferralCode,
  LoyaltyReminderDialog,
  TrustPilotMicroReviewCount,
  useLoyaltyReminderDialog,
  useTranslations,
} from "@jti/ui";
import { useAuthHrefResolver } from "@jti/ui/providers/AuthRoutingProvider";

import { CartOverviewContainer } from "@checkout/components/CartOverviewContainer";
import { CartPaymentSummary } from "@checkout/components/CartPaymentSummary";
import PricesFooter from "@checkout/components/PricesFooter";
import { PromoCode } from "@checkout/components/PromoCode";
import { SummarySubscription } from "@checkout/components/SummarySubscription";
import { SummarySubscriptionFooter } from "@checkout/components/SummarySubscription/SummarySubscriptionFooter/SummarySubscriptionFooter";
import { useLoyaltyCustomerState } from "@checkout/hooks/useLoyaltyCustomerState";
import { useLoyaltyProductPoints } from "@checkout/hooks/useLoyaltyProductPoints";
import { useShouldShowLoyaltyReminderDialog } from "@checkout/hooks/useShouldShowLoyaltyReminderDialog";
import { useSyncReferralStatus } from "@checkout/hooks/useSyncReferralStatus";
import { AsideLayout } from "@checkout/layouts/AsideLayout";
import { categorizeItems } from "@checkout/utils/categorizeItems";
import { filterItemsByErrorCode } from "@checkout/utils/getCartErrors";
import getCartItemsCount from "@checkout/utils/getCartItemsCount";

import { useCartTradeInError } from "../hooks/useCartTradeInError";

import styles from "./CartPageAside.module.scss";

type CartPageAsideProps = {
  isCartItemsLimitReached: boolean;
  isAgeVerificationErrorVisible: boolean;
  onNavigate?: (url: string) => void;
};

const navigateWithBrowser = (url: string) => {
  window.location.href = url;
};

export const CartPageAside: FC<CartPageAsideProps> = ({
  isCartItemsLimitReached,
  isAgeVerificationErrorVisible,
  onNavigate = navigateWithBrowser,
}) => {
  useLoyaltyCustomerState();
  useSyncReferralStatus();

  const history = useHistory();
  const dispatch = useDispatch();
  const { __ } = useTranslations();
  const { loyalty, cartOverviewTier } = useCheckoutConfiguration();
  const config = useConfig();
  const resolveAuthHref = useAuthHrefResolver();
  const user = getUser();

  const { promoCode } = useSelector((store) => store.cart);
  const freeTrialID = useSelector((store) => store.freeTrialCart.id);
  const items = useSelector((state) => state.cart.items);
  const itemsCount = getCartItemsCount(items);
  const hasOutOfStockItems = useSelector((state) => state.cart.items.some(({ status }) => status === "OUT_OF_STOCK"));
  const isCartOnlyStandaloneGifts = useSelector(selectIsCartOnlyStandaloneGifts);
  const restrictedSinglePurchaseItems = filterItemsByErrorCode(items, ICartItemErrorType.LifetimePurchaseRestricted);
  const qtyPending = useSelector(selectQtyPending);
  const { setError, isTradeInItemInCart, isTradeInItemSelected } = useCartTradeInError();

  const totalLoyaltyPoints = useLoyaltyProductPoints(items);

  const shouldShowLoyaltyReminderDialog = useShouldShowLoyaltyReminderDialog(loyalty);

  const {
    isOpen: isReminderDialogOpen,
    open: openReminderDialog,
    close: closeReminderDialog,
  } = useLoyaltyReminderDialog();

  const { subscriptionItems } = categorizeItems(items);
  const shouldRenderPromoCodeBlock =
    !config.hidePromoCodeBlockOnCheckoutLandingPage && itemsCount > 0 && promoCode.isVisible;

  const startCheckoutProcess = useCallback(() => {
    if (!user?.email) {
      const loginHref = resolveAuthHref({
        route: AUTH_ROUTE_PATHS.login,
        search: { returnUrl: `${config.CART_CHECKOUT_PAGE_URL}#${standardCheckoutRoutes.root}` },
      });
      redirectToAuthWithBack(loginHref);
      return;
    }

    getSessionStorage().set("loyaltyCartPoints", totalLoyaltyPoints);
    dispatch(setReady());

    if (config.isCheckoutV2Enabled) {
      dispatch(checkoutV2Slice.actions.resetState());
      onNavigate(config.CHECKOUT_PAGE_URL);
    } else {
      history.push(standardCheckoutRoutes.shipping);
    }
  }, [
    user?.email,
    totalLoyaltyPoints,
    dispatch,
    config.isCheckoutV2Enabled,
    config.CHECKOUT_PAGE_URL,
    config.CART_CHECKOUT_PAGE_URL,
    history,
    onNavigate,
    resolveAuthHref,
  ]);

  const onLoyaltyReminderPrimaryButtonClick = useCallback(() => {
    closeReminderDialog();
    startCheckoutProcess();
  }, [closeReminderDialog, startCheckoutProcess]);

  const onLoyaltyReminderSecondaryButtonClick = useCallback(() => {
    closeReminderDialog();
    if (!isServerSide() && loyalty.reminderDialogSecondaryButtonLink) {
      window.location.href = loyalty.reminderDialogSecondaryButtonLink;
    }
  }, [closeReminderDialog, loyalty.reminderDialogSecondaryButtonLink]);

  const onCheckoutButtonClick = useCallback(() => {
    if (isTradeInItemInCart && !isTradeInItemSelected) {
      setError(__("cart.trade_in.error_message"));
      return;
    }

    if (shouldShowLoyaltyReminderDialog) {
      openReminderDialog();
      return;
    }

    startCheckoutProcess();
    dispatch(clearFreeGiftsErrors(undefined));
  }, [
    shouldShowLoyaltyReminderDialog,
    openReminderDialog,
    startCheckoutProcess,
    isTradeInItemInCart,
    isTradeInItemSelected,
    setError,
  ]);

  const onReferralCodeApplied = () => {
    if (isServerSide()) {
      return;
    }

    window.location.href = window.location.href.split("#")[0];
  };

  return (
    <>
      <AsideLayout
        data-testid="regularCartSummaryBlock"
        slot1={
          <>
            {!freeTrialID && loyalty.showMgm ? (
              <div className={styles.mgmWrapper}>
                <LoyaltyReferralCode onCodeApplied={onReferralCodeApplied} source="cart" />
              </div>
            ) : null}
            {shouldRenderPromoCodeBlock ? <PromoCode /> : null}
          </>
        }
        paymentSummarySlot={<CartPaymentSummary showFreeShippingProgressBar />}
        slot4={<CartOverviewContainer cartOverviewTier={cartOverviewTier} totalLoyaltyPoints={totalLoyaltyPoints} />}
        slot0={
          subscriptionItems.length > 0 ? (
            <SummarySubscription
              items={subscriptionItems}
              isCollapsible={false}
              showProductList={false}
              nonCollapsibleContent={
                /*
                  override to always show billing disclaimer on the cart page
                */
                <SummarySubscriptionFooter displayPointsSection={false} showSidebarBillingDisclaimer />
              }
            />
          ) : null
        }
        slot2={
          <>
            <PricesFooter
              disabled={
                hasOutOfStockItems ||
                isCartItemsLimitReached ||
                isAgeVerificationErrorVisible ||
                !itemsCount ||
                qtyPending ||
                restrictedSinglePurchaseItems.length > 0 ||
                hasOngoingSubscriptionError(items) ||
                isCartOnlyStandaloneGifts
              }
              onCheckout={onCheckoutButtonClick}
            />
            <TrustPilotMicroReviewCount className={styles.trustPilotWidget} />
          </>
        }
      />

      {shouldShowLoyaltyReminderDialog && (
        <LoyaltyReminderDialog
          isOpened={isReminderDialogOpen}
          title={loyalty.reminderDialogTitle}
          subtitle={loyalty.reminderDialogSubtitle}
          text={loyalty.reminderDialogText}
          primaryButtonText={__("cart.button.proceed_to_checkout")}
          secondaryButtonText={loyalty.reminderDialogSecondaryButtonText}
          onPrimaryButtonClick={onLoyaltyReminderPrimaryButtonClick}
          onSecondaryButtonClick={onLoyaltyReminderSecondaryButtonClick}
          onClose={closeReminderDialog}
        />
      )}
    </>
  );
};
